/* eslint-disable @1js/office-online-rules/no-v8-imports-for-fluentui */
import type * as React from "react";

import { DirectionalHint, elementContains, KeyCodes } from "@fluentui/react";

import type {
  FunctionOrValue,
  SetTooltipProps,
  UILocation,
} from "@1js/acui-common";
import {
  findParentItemByClass,
  getHoverableTooltipsPreference,
  performOnNextFrame,
} from "@1js/acui-common";

import type { AppTooltipHostProps } from "./AppToolTipHost.Props";
import { AppContext } from "@1js/acui-context";

export class AppTooltipHost {
  private updateTooltipsHidden: (hideAllTooltips?: boolean) => void;
  private setTooltipElement: (tooltipProps: SetTooltipProps) => void;
  private targetElement: HTMLElement;
  private anchorElement?: React.RefObject<HTMLElement>;
  private controlId: string;
  private controlLabel: FunctionOrValue<string>;
  private tooltipTopMargin: number;
  private isMenuComponent?: boolean;
  private targetHoverElement?: HTMLElement;
  private tooltipDirection?: DirectionalHint;
  private tooltipDirectionForRTL?: DirectionalHint;
  private static classListItem = "ms-OverflowSet-item";
  private tooltipIsOpen: boolean = false;
  private inTouchMode: boolean = false;
  private timeoutId: number = 0;
  private readonly longPressMs: number = 500;
  private performSetOnNextFrame: boolean;
  private performOnNextFrameCallbackId = -1;
  private targetOverflowSetItem: boolean = false;
  private location: UILocation | undefined = undefined;

  // This allows us to access AppContext directly from this.context
  static contextType: any = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  constructor(appTooltipHostProps: AppTooltipHostProps) {
    this.updateTooltipsHidden = appTooltipHostProps.updateTooltipsHidden;
    this.setTooltipElement = appTooltipHostProps.setTooltipElement;
    this.controlId = appTooltipHostProps.controlId;
    this.controlLabel = appTooltipHostProps.controlLabel;
    this.tooltipTopMargin = appTooltipHostProps.tooltipTopMargin
      ? appTooltipHostProps.tooltipTopMargin
      : 0;
    this.isMenuComponent = appTooltipHostProps.isMenuComponent;
    this.tooltipDirection = appTooltipHostProps.tooltipDirection;
    this.tooltipDirectionForRTL = appTooltipHostProps.tooltipDirectionForRTL;
    this.anchorElement = appTooltipHostProps.anchorElement;
    this.performSetOnNextFrame = !!appTooltipHostProps.performSetOnNextFrame;
    this.targetOverflowSetItem = !!appTooltipHostProps.targetOverflowSetItem;
    this.location = appTooltipHostProps.location;

    const htmlElement =
      appTooltipHostProps.htmlElement ||
      (appTooltipHostProps.refElement?.current as HTMLElement);
    this.targetElement = this.targetOverflowSetItem
      ? findParentItemByClass(htmlElement, AppTooltipHost.classListItem) ||
        htmlElement
      : htmlElement;

    // Some buttons may need to pass a different element for hover than for key focus. For example, a
    // checkbox or combobox inside of menu needs to make sure the entire element displays a tooltip
    // when hovered
    const { hoverElement } = appTooltipHostProps;
    if (hoverElement) {
      this.targetHoverElement = hoverElement.parentElement?.classList.contains(
        AppTooltipHost.classListItem
      )
        ? hoverElement.parentElement
        : hoverElement;
    }
  }

  public updateTooltip(newTooltip: FunctionOrValue<string>): void {
    if (this.controlLabel === newTooltip) {
      return;
    }
    this.controlLabel = newTooltip;
    // If the tooltip is currently open, update it with the new props
    if (this.tooltipIsOpen) {
      this.setTooltip();
    }
  }

  public updateTooltipAnchor(anchor: React.RefObject<HTMLElement>) {
    if (this.anchorElement === anchor) {
      return;
    }
    this.anchorElement = anchor;
    // If the tooltip is currently open, update it with the new props
    if (this.tooltipIsOpen) {
      this.setTooltip();
    }
  }

  public registerTooltipHostActionsForMouseEvents = (element: HTMLElement) => {
    if (element) {
      // pointer events are used instead of mouse events to support tooltips on disabled controls.
      element.addEventListener("mouseenter", this.setTooltip, false);
      element.addEventListener("mousedown", this.setTooltipUndefined, false);

      // This is needed in the case where the tooltip is being removed after being scheduled,
      // but before was actually created (e.g. setTooltipElement was called), which means
      // dismissTooltipCallback isn't hooked up to mouseleave
      if (this.performSetOnNextFrame) {
        element.addEventListener("mouseleave", this.mouseLeave, false);
      }
    }
  };

  public registerTooltipHostActions = () => {
    this.registerTooltipHostActionsForMouseEvents(
      this.targetHoverElement ? this.targetHoverElement : this.targetElement
    );

    if (this.targetElement) {
      this.targetElement.addEventListener("keydown", this.onKeyDown, false);
      this.targetElement.addEventListener("focusin", this.focusIn, false);
      this.targetElement.addEventListener("focusout", this.focusOut, false);
      this.targetElement.addEventListener(
        "pointerenter",
        this.onPointerEnter,
        false
      );
      this.targetElement.addEventListener(
        "pointerup",
        this.onPointerDismiss,
        false
      );
      this.targetElement.addEventListener(
        "pointerout",
        this.onPointerDismiss,
        false
      );
    }
  };

  public unregisterTooltipHostActionsForMouseEvents = (
    element: HTMLElement
  ) => {
    if (element) {
      element.removeEventListener("mouseenter", this.setTooltip, false);
      element.removeEventListener("mousedown", this.setTooltipUndefined, false);

      if (this.performSetOnNextFrame) {
        element.removeEventListener("mouseleave", this.mouseLeave, false);
      }
    }
  };

  public unregisterTooltipHostActions = () => {
    this.unregisterTooltipHostActionsForMouseEvents(
      this.targetHoverElement ? this.targetHoverElement : this.targetElement
    );

    if (this.targetElement) {
      this.targetElement.removeEventListener("keydown", this.onKeyDown, false);
      this.targetElement.removeEventListener("focusin", this.focusIn, false);
      this.targetElement.removeEventListener("focusout", this.focusOut, false);
      this.targetElement.removeEventListener(
        "pointerenter",
        this.onPointerEnter,
        false
      );
      this.targetElement.removeEventListener(
        "pointerup",
        this.onPointerDismiss,
        false
      );
      this.targetElement.removeEventListener(
        "pointerout",
        this.onPointerDismiss,
        false
      );
      // Set the tooltip to be undefined in the event that a control is unmounted before it loses the tooltip via mouse/keyboard blur.
      this.setTooltipUndefined();
    }
  };

  public hideAllTooltips = (hideAllTooltips: boolean) => {
    this.updateTooltipsHidden(hideAllTooltips);
  };

  public setTooltipUndefined = () => {
    if (!this.tooltipIsOpen) {
      return;
    }

    this.setTooltipInternal(
      {
        element: undefined,
        label: undefined,
        id: undefined,
        tooltipTopMargin: undefined,
        isMenuComponent: this.isMenuComponent,
        tooltipDirection: this.tooltipDirection,
        tooltipDirectionForRTL: this.tooltipDirectionForRTL,
        dismissTooltipCallback: undefined,
        location: undefined,
      },
      false /* tooltipIsOpen */
    );
  };

  private mouseLeave = () => {
    // When hoverable tooltips are enabled, onMouseLeave is covered in the AppTooltipManager
    if (!getHoverableTooltipsPreference()) {
      this.setTooltipUndefined();
    }
  };

  private focusIn = () => {
    this.setTooltip();
  };

  private focusOut = (ev: FocusEvent) => {
    /** The focusOut event bubbles so for menu items, when we show the tooltip
     * and then try to move the mouse over the tooltip, we get a focusOut event
     * as the mouse goes to the menu on the way to the tooltip. This check to see
     * if the target contains the menu element ensures the tooltip will stay open
     * as we try to hover over it. If hovering on tooltips is disabled, we get a
     * mouseOut event elsewhere and dismiss the tooltip.
     */
    if (this.isMenuComponent && ev.relatedTarget) {
      if (
        elementContains(ev.relatedTarget as HTMLElement, this.targetElement)
      ) {
        return;
      }
    }
    this.setTooltipUndefined();
  };

  public setTooltip = () => {
    this.setTooltipInternal(
      {
        element: this.anchorElement?.current ?? this.targetElement,
        label: this.controlLabel,
        id: this.controlId,
        tooltipTopMargin: this.tooltipTopMargin,
        isMenuComponent: this.isMenuComponent,
        tooltipDirection: this.inTouchMode
          ? DirectionalHint.topCenter
          : this.tooltipDirection,
        tooltipDirectionForRTL: this.inTouchMode
          ? DirectionalHint.topCenter
          : this.tooltipDirectionForRTL,
        dismissTooltipCallback: this.setTooltipUndefined,
        location: this.location,
      },
      true /* tooltipIsOpen */
    );
  };

  private setTooltipInternal = (
    setTooltipProps: SetTooltipProps,
    tooltipIsOpen: boolean
  ) => {
    // If we have already scheduled a callback to run, cancel it
    // before scheduling a new one
    if (this.performOnNextFrameCallbackId > -1) {
      window.cancelAnimationFrame(this.performOnNextFrameCallbackId);
      this.performOnNextFrameCallbackId = -1;
    }

    const setTooltipElementCallback = () => {
      this.setTooltipElement(setTooltipProps);
      this.performOnNextFrameCallbackId = -1;

      // Need to return -1 here since this can be run synchronously
      // if performSetOnNextFrame is false
      return this.performOnNextFrameCallbackId;
    };

    this.performOnNextFrameCallbackId = this.performSetOnNextFrame
      ? performOnNextFrame(setTooltipElementCallback)
      : setTooltipElementCallback();

    // This needs to be set outside of the next frame callback
    // so that other calls will know that the code has been scheduled to open/close
    this.tooltipIsOpen = tooltipIsOpen;
  };

  private onKeyDown = (ev: KeyboardEvent): void => {
    if (ev && ev.type === "keydown" && ev.which === KeyCodes.enter) {
      this.setTooltipUndefined();
    }

    // toggle tooltip on/off with the shortcut
    if (ev.ctrlKey && ev.shiftKey && ev.which === KeyCodes.f10) {
      this.tooltipIsOpen ? this.setTooltipUndefined() : this.setTooltip();
    }
  };

  /**
   * Display tooltip if it's a long press
   */
  private onPointerEnter = (ev: PointerEvent): void => {
    if (!ev || ev.pointerType !== "touch") {
      return;
    }

    this.inTouchMode = true;
    this.timeoutId = window.setTimeout(() => {
      this.timeoutId = 0;
      this.setTooltip();
    }, this.longPressMs);
  };

  /**
   * Remove tooltip on touch release
   */
  private onPointerDismiss = (ev: PointerEvent): void => {
    if (!ev || ev.pointerType !== "touch") {
      return;
    }

    this.inTouchMode = false;
    clearTimeout(this.timeoutId);

    // Touch can trigger a focus event,
    // so ensure the focus handler isn't called
    if (this.tooltipIsOpen) {
      ev.preventDefault();
    }

    this.setTooltipUndefined();
  };
}
